/**
 * TutorialCompletionModal Component (AC-672, AC-1015)
 *
 * Celebration modal shown when a tutorial is completed.
 * Shows tutorial-specific content based on the active tutorial ID.
 *
 * Features:
 * - Congratulations message with celebration visual
 * - Tutorial-specific summary of topics covered
 * - "Start Building" primary action (close and continue)
 * - "Explore More Tutorials" secondary action (navigates to Bootcamp)
 * - Accessible modal with proper ARIA attributes
 * - Focus trap within modal
 * - Closes on Escape key and backdrop click
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { TutorialCompletionModalProps } from '../../types/tutorial';

/**
 * Tutorial-specific completion content.
 */
interface TutorialCompletionContent {
  description: string;
  topics: string[];
  ctaLabel: string;
}

const TUTORIAL_COMPLETION_CONTENT: Record<string, TutorialCompletionContent> = {
  core_workflow: {
    description: 'You have successfully completed the Movie Summarizer tutorial and learned the basics of Agent Commander.',
    topics: [
      'Creating and managing Projects',
      'Configuring AI Agents',
      'Building and running Workflows',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  book_analyzer: {
    description: 'You have successfully completed the Book Analyzer tutorial and learned how to use structured output schemas.',
    topics: [
      'Creating schemas for structured AI output',
      'Using inline agent creation from the workflow editor',
      'Turning free-form AI responses into organized data',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  deterministic_story_flow: {
    description: 'You have successfully completed the Deterministic Story Flow tutorial and built a multi-agent pipeline.',
    topics: [
      'Creating agents inline from the workflow editor',
      'Building structured output models for agent responses',
      'Using conditional steps for decision logic',
      'Building multi-agent pipelines with sequential processing',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  mcp_browser_intro: {
    description: 'You have successfully completed the MCP Servers tutorial and learned how agents use external tools through MCP.',
    topics: [
      'Understanding MCP (Model Context Protocol) servers',
      'Using Browser Automation MCP tools for web navigation and screenshots',
      'Building multi-step workflows with override instructions',
      'Viewing visual output (screenshots) from MCP tools',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  template_web_research: {
    description: 'You have successfully completed the Web Research tutorial and learned how to use agent templates.',
    topics: [
      'Browsing and selecting agent templates',
      'Creating agents from templates directly in the workflow editor',
      'How templates provide pre-configured agents with tools and instructions',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  financial_research_agent: {
    description: 'You have successfully built a Financial Research Agent pipeline combining every advanced pattern in Agent Commander.',
    topics: [
      'Building structured output schemas with nested types (list of schemas)',
      'Using parallel fan-out to process lists concurrently',
      'Giving agents web search capabilities for real-time data',
      'The agents-as-tools pattern for specialist delegation',
      'Understanding step intents: Create vs Enhance',
      'Viewing tool calls in the Tool Usage tab',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
  email_sorter: {
    description: 'You have successfully built an AI Email Sorter that automatically organizes your inbox on a schedule.',
    topics: [
      'Connecting email accounts via MCP server integration',
      'Using Auto Prompt for fully automated workflows',
      'Creating agents with MCP tools for email management',
      'Setting up cron scheduling for recurring automation',
    ],
    ctaLabel: 'Explore More Tutorials',
  },
};

const DEFAULT_CONTENT: TutorialCompletionContent = {
  description: 'You have successfully completed the tutorial.',
  topics: [],
  ctaLabel: 'Explore More Tutorials',
};

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

/**
 * TutorialCompletionModal - Tutorial completion celebration modal.
 */
const TutorialCompletionModal: React.FC<TutorialCompletionModalProps> = ({
  isOpen,
  onClose,
  onExploreHelp,
  tutorialId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);

  const content = useMemo(() => {
    if (tutorialId && TUTORIAL_COMPLETION_CONTENT[tutorialId]) {
      return TUTORIAL_COMPLETION_CONTENT[tutorialId];
    }
    return DEFAULT_CONTENT;
  }, [tutorialId]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        startButtonRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (event: FocusEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        startButtonRef.current?.focus();
      }
    };

    document.addEventListener('focusin', handleFocusTrap);
    return () => {
      document.removeEventListener('focusin', handleFocusTrap);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <>
      {/* Backdrop overlay - closes on click */}
      <div
        data-testid="modal-backdrop"
        className="fixed inset-0 bg-black/50 z-50 overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-modal-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ position: 'fixed', display: 'flex' }}
      >
        <div
          className="bg-white dark:bg-cap-dark rounded-xl shadow-2xl max-w-[calc(100%-2rem)] sm:max-w-md w-full p-4 sm:p-6 transform transition-all duration-300 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
          style={{
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          {/* Celebration Icon */}
          <div className="flex justify-center mb-4">
            <div
              data-testid="celebration-icon"
              className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center celebration"
              aria-hidden="true"
              style={{
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            >
              <svg
                className="w-7 h-7 sm:w-10 sm:h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h2
            id="completion-modal-title"
            className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-warm-white mb-2"
          >
            Congratulations!
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
            {content.description}
          </p>

          {/* Topics covered */}
          {content.topics.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                You covered:
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                {content.topics.map((topic) => (
                  <li key={topic} className="flex items-center">
                    <CheckIcon />
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bootcamp suggestion */}
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
            Check out the Bootcamp for more tutorials and documentation.
          </p>

          {/* Action buttons */}
          <div className="flex flex-col space-y-2">
            <button
              ref={startButtonRef}
              type="button"
              onClick={onClose}
              className="w-full min-h-[48px] py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 primary btn-primary"
            >
              Start Building
            </button>
            <button
              type="button"
              onClick={onExploreHelp}
              className="w-full min-h-[48px] py-2 px-4 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-lg"
            >
              {content.ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Render via portal for proper z-index layering
  return createPortal(modalContent, document.body);
};

export default TutorialCompletionModal;
